import React, { useEffect, useState } from "react";
import { client } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const FIELDS = {
  customer: [["name", "Customer name *"], ["code", "Customer code *"], ["main_contact", "Main contact"],
             ["phone", "Phone"], ["email", "Email"], ["qb_name", "QuickBooks name"], ["qb_id", "QuickBooks ID"],
             ["notes", "Notes", true]],
  site: [["name", "Site name *"], ["address", "Address"], ["on_site_contact", "Site contact"], ["phone", "Phone"],
         ["email", "Email"], ["access_notes", "Access notes", true]],
  area: [["name", "Area name *"], ["contact_name", "Area contact"], ["phone", "Phone"],
         ["access_notes", "Access notes", true]],
};
const PATHS = { customer: "customers", site: "sites", area: "areas" };
const TITLES = { customer: "Edit customer", site: "Edit site", area: "Edit area" };

export function EditRegisterDialog({ kind, record, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!record) return;
    setForm(Object.fromEntries(FIELDS[kind].map(([key]) => [key, record[key] || ""])));
    setError("");
  }, [kind, record]);
  if (!record) return null;
  const codeChanged = kind === "customer" && (form.code || "").trim().toUpperCase() !== (record.code || "");

  const save = async () => {
    const body = Object.fromEntries(FIELDS[kind].map(([key]) => [key, form[key] ?? ""])
      .filter(([key, value]) => value !== (record[key] || "")));
    try {
      setSaving(true); setError("");
      await client.patch(`/${PATHS[kind]}/${record.id}`, body);
      toast.success("Details saved");
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save the details");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[560px] max-h-[88vh] overflow-y-auto" data-testid={`edit-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{TITLES[kind]}</DialogTitle>
          <DialogDescription data-testid={`edit-${kind}-frozen-note`}>
            Completed job reports keep the details they were completed with. Changes here apply to new and open jobs.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS[kind].map(([key, label, long]) => (
            <div key={key} className={long ? "md:col-span-2" : ""}>
              <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>{label}</Label>
              {long ? (
                <Textarea rows={3} className="mt-1 rounded-[10px] text-[13.5px]" value={form[key] || ""}
                          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} data-testid={`edit-${kind}-${key}`} />
              ) : (
                <Input className="mt-1 rounded-[10px]" value={form[key] || ""}
                       onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} data-testid={`edit-${kind}-${key}`} />
              )}
            </div>
          ))}
        </div>
        {codeChanged && (
          <div className="text-[12.5px] mt-2 rounded-[10px] px-3 py-2" style={{ background: "#FFF6E5", color: "#96650A" }}
               data-testid="edit-customer-code-warning">
            Spreadsheet imports match customers on this code. Use the new code in future import files.
          </div>
        )}
        {error && <div className="text-[12.5px] mt-2" style={{ color: "#B42318" }} data-testid={`edit-${kind}-error`}>{error}</div>}
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={onClose} data-testid={`edit-${kind}-cancel`}>Cancel</Button>
          <Button className="bf-btn-primary px-4" disabled={saving} onClick={save} data-testid={`edit-${kind}-save`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
